/**
 * Die **Ticketzelle**: die im Commit-Kommentar erkannten Ticketverweise einer
 * Listenzeile, als Links (#113; ADR-0027).
 *
 * **Eine eigene Spalte und kein Link in der Kommentarvorschau.** Der Einwand aus
 * #101 fällt damit nicht weg, er ist beantwortet: Zwei Ziele an *einer* Stelle
 * sind ein Rätsel — welches gemeint war, entschiede die Pixelbreite einer
 * Ticketnummer —, zwei Ziele in **zwei Spalten** sind eine Tabelle.
 *
 * **An einer Stelle für beide Listen.** Der Revisionsstrom und der Befundvorrat
 * zeigen denselben Verweis; getrennte Umsetzungen wären zwei Darstellungen
 * desselben Dings, sobald eine davon nachgezogen wird und die andere nicht —
 * dieselbe Entscheidung wie bei der Befundzelle daneben.
 *
 * **Das Kürzel steht da, das volle Ziel im Titel.** `DEV-435` ist der Name, den
 * dieser Vorgang in *jeder* Zeile trägt — er kommt aus dem Kürzel seiner
 * Ticketliste und nicht aus dem Text, an dem er erkannt wurde (#131; ADR-0030),
 * denn drei Formen finden denselben Vorgang. `yQos/dev-liste#435` ist, was
 * gemeint ist — und weil es Nummer 435 in beiden Ticketlisten gibt, ist die Liste
 * keine Beigabe. Für die Spalte ist sie zu breit, für den Titel genau richtig.
 * Ohne eingetragenes Kürzel steht sie auch in der Spalte.
 *
 * **Der Anker hält die Ereignisweitergabe an.** Er sitzt in einer Zeile, deren
 * Klick die Revision öffnet; hier soll er nur das eine tun, worauf er zeigt. Ein
 * Klick daneben öffnet sie weiterhin.
 *
 * **Gefragt wird das Ticketsystem nichts.** Ob es den Vorgang gibt, ist
 * ungeprüft: Eine Liste mit dreißig Zeilen löste sonst dreißig Anfragen an eine
 * fremde Gegenstelle aus. Nachgesehen wird einmal, beim Öffnen des
 * Zustelldialogs.
 */

import type { ReactElement } from "react";

import type { IssueReference } from "../api/client";

/**
 * Wie viele Kürzel die Spalte zeigt, bevor sie zählt.
 *
 * Der Regelfall ist einer; drei machten die Spalte breiter als die
 * Kommentarvorschau daneben, und die Zeile soll einzeilig bleiben.
 */
const SHOWN = 2;

/**
 * `references` leer heißt: Dieser Kommentar nennt keinen Vorgang — oder es gibt
 * keinen Kommentar. Die Zelle bleibt dann **leer**: Kein Platzhalter, kein
 * Strich; eine Spalte, die in der Hälfte der Zeilen „—" sagt, ist Lärm.
 */
export function IssueReferenceCell({
  references,
}: {
  references: IssueReference[];
}): ReactElement | null {
  if (references.length === 0) {
    return null;
  }

  const shown = references.slice(0, SHOWN);
  const rest = references.length - shown.length;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "baseline",
        gap: "var(--sp-4)",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      {shown.map((one) => (
        <a
          key={one.address}
          href={one.address}
          target="_blank"
          rel="noreferrer"
          title={targetOf(one)}
          // Die Zeile öffnet sonst die Revision; hier soll der Link nur das eine
          // tun, worauf er zeigt.
          onClick={(event) => event.stopPropagation()}
          style={{ overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {one.label}
        </a>
      ))}
      {rest === 0 ? null : (
        // **Kein Link.** Wer alle braucht, ist in der Revision besser dran, und
        // dort stehen sie ohnehin alle; im Titel stehen sie auch hier.
        <span
          title={references.map(targetOf).join(" · ")}
          style={{ flex: "none", fontSize: "var(--fs-11)", color: "var(--fg-6)" }}
        >
          {`+${rest}`}
        </span>
      )}
    </span>
  );
}

/** Das volle Ziel: die Ticketliste und die Nummer darin. */
function targetOf(reference: IssueReference): string {
  return `${reference.ticketList}#${reference.number}`;
}
